import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { FeeChangeCommandHandlerResolver } from '../../service/command/fee-change-command-handler-resolver.service';
import { FeeChangeService } from '../../service/data/fee-change.service';
import { ChangeType, FeeChange } from '../../model/fee-change';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'fee-change-form',
    templateUrl: './fee-change-form.component.html',
    styleUrls: ['./fee-change-form.component.scss'],
    providers: [FeeChangeCommandHandlerResolver, { provide: DataService, useExisting: FeeChangeService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FeeChangeFormComponent extends FormComponentBase<FeeChange> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _nativeWindow: NativeWindowService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    factory: ColumnDefinitionFactory,
    handlerResolver: FeeChangeCommandHandlerResolver,
    private service: FeeChangeService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);

    this.addDeleteMenu = false;

    this.leaseRowColumnDefinitions = [
      factory.create('COMMON.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName', ColumnVisibility.optional),
      factory.create('COMMON.RENTAL_OBJECT', 'rentalObjectStreet', ColumnVisibility.optional),
      factory.create('COMMON.RENTAL_OBJECT_TYPE', 'rentalObjectTypeName'),
      factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      factory.create('COMMON.PROPERTY', 'propertyNumberAndName'),
      factory.createMoney('FEE_CHANGE.OLD_YEAR_RENT', 'oldYearRent'),
      factory.createMoney('FEE_CHANGE.NEW_YEAR_RENT', 'newYearRent'),
      factory.createMoney('FEE_CHANGE.DIFFERENCE', 'difference'),
      factory.createDecimal('CONST.RENTAL_CHANGES_UNIT_PERCENTAGE', 'percentageChange'),
      factory.createMoney('FEE_CHANGE.RETROACTIVE_AMOUNT', 'retroactiveAmount'),
      factory.create('ARTICLE.CODE', 'articleNumber', ColumnVisibility.optional),
      factory.create('ARTICLE.INVOICE_TEXT', 'invoiceRowText', ColumnVisibility.optional),
    ];

    this.leaseRowIndexColumnDefinitions = [
      factory.create('COMMON.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      factory.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName', ColumnVisibility.optional),
      factory.create('COMMON.RENTAL_OBJECT', 'rentalObjectStreet', ColumnVisibility.optional),
      factory.create('COMMON.RENTAL_OBJECT_TYPE', 'rentalObjectTypeName'),
      factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      factory.create('COMMON.PROPERTY', 'propertyNumberAndName'),
      factory.createMoney('FEE_CHANGE.OLD_YEAR_RENT', 'oldYearRent'),
      factory.createMoney('FEE_CHANGE.NEW_YEAR_RENT', 'newYearRent'),
      factory.createMoney('FEE_CHANGE.DIFFERENCE', 'difference'),
      factory.createDecimal('CONST.RENTAL_CHANGES_UNIT_PERCENTAGE', 'percentageChange'),
      factory.create('ARTICLE.CODE', 'articleNumber', ColumnVisibility.optional),
      factory.create('ARTICLE.INVOICE_TEXT', 'invoiceRowText', ColumnVisibility.optional),
      factory.create('LEASE_AGREEMENT_ROW.INDEX_CLAUSE', 'indexClauseName'),
      factory.createMoney('LEASE_AGREEMENT_ROW.BASE_RENT', 'baseYearRent', ColumnVisibility.optional),
      factory.createMoney('LEASE_AGREEMENT_ROW.INDEX_ADDITION', 'indexAddition', ColumnVisibility.optional),
      factory.createDecimal('LEASE_AGREEMENT_ROW.INDEX_SHARE', 'indexShare', ColumnVisibility.optional),
      factory.create('LEASE_AGREEMENT_ROW.BASE_INDEX_YEAR', 'baseIndexYear', ColumnVisibility.optional),
    ];

    this.templateRowColumnDefinitions = [
      factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      factory.create('COMMON.RENTAL_OBJECT', 'rentalObjectStreet', ColumnVisibility.optional),
      factory.create('COMMON.RENTAL_OBJECT_TYPE', 'rentalObjectTypeName'),
      factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      factory.create('COMMON.PROPERTY', 'propertyNumberAndName'),
      factory.createMoney('FEE_CHANGE.OLD_YEAR_RENT', 'oldYearRent'),
      factory.createMoney('FEE_CHANGE.NEW_YEAR_RENT', 'newYearRent'),
      factory.createMoney('FEE_CHANGE.DIFFERENCE', 'difference'),
      factory.createDecimal('CONST.RENTAL_CHANGES_UNIT_PERCENTAGE', 'percentageChange'),
      factory.create('ARTICLE.CODE', 'articleNumber', ColumnVisibility.optional),
      factory.create('ARTICLE.INVOICE_TEXT', 'invoiceRowText', ColumnVisibility.optional),
    ];

    this.templateRowIndexColumnDefinitions = [
      factory.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      factory.create('COMMON.RENTAL_OBJECT', 'rentalObjectStreet', ColumnVisibility.optional),
      factory.create('COMMON.RENTAL_OBJECT_TYPE', 'rentalObjectTypeName'),
      factory.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      factory.create('COMMON.PROPERTY', 'propertyNumberAndName'),
      factory.createMoney('FEE_CHANGE.OLD_YEAR_RENT', 'oldYearRent'),
      factory.createMoney('FEE_CHANGE.NEW_YEAR_RENT', 'newYearRent'),
      factory.createMoney('FEE_CHANGE.DIFFERENCE', 'difference'),
      factory.createDecimal('CONST.RENTAL_CHANGES_UNIT_PERCENTAGE', 'percentageChange'),
      factory.create('ARTICLE.CODE', 'articleNumber', ColumnVisibility.optional),
      factory.create('ARTICLE.INVOICE_TEXT', 'invoiceRowText', ColumnVisibility.optional),
      factory.create('LEASE_AGREEMENT_ROW.INDEX_CLAUSE', 'indexClauseName'),
      factory.createMoney('LEASE_AGREEMENT_ROW.BASE_RENT', 'baseYearRent', ColumnVisibility.optional),
      factory.createMoney('LEASE_AGREEMENT_ROW.INDEX_ADDITION', 'indexAddition', ColumnVisibility.optional),
      factory.createDecimal('LEASE_AGREEMENT_ROW.INDEX_SHARE', 'indexShare', ColumnVisibility.optional),
      factory.create('LEASE_AGREEMENT_ROW.BASE_INDEX_YEAR', 'baseIndexYear', ColumnVisibility.optional),
    ];
  }

  changeTypes = [
    { caption: 'FEE_CHANGE.CHANGE_TYPES.FEE_CHANGE', value: false },
    { caption: 'FEE_CHANGE.CHANGE_TYPES.IMPORT', value: true },
  ];

  leaseRowColumnDefinitions: ColumnDefinition[];
  leaseRowIndexColumnDefinitions: ColumnDefinition[];
  templateRowColumnDefinitions: ColumnDefinition[];
  templateRowIndexColumnDefinitions: ColumnDefinition[];

  exportLeaseAgreementsExcel(): void {
    this.AddSubscription(this.service.getExcelLeaseAgreements(this.dataContext.id).subscribe(file => {
      this._nativeWindow.save(file);
    }));
  }

  exportTemplateRowsExcel(): void {
    this.AddSubscription(this.service.getExcelTemplateRows(this.dataContext.id).subscribe(file => {
      this._nativeWindow.save(file);
    }));
  }

  get isImport(): boolean {
    return this.dataContext && this.dataContext.changeTypeKind == ChangeType.Import;
  }

  get isIndexChange(): boolean {
    return this.dataContext && this.dataContext.changeTypeKind == ChangeType.Index;
  }

  protected createModel(): FeeChange {
    return new FeeChange();
  }

  protected getFormGroup(): FormGroup {
    return this._fb.group({
      caption: this._fc.create('FEE_CHANGE.CAPTION', this.dataContext.caption),
      performedAt: this._fc.createReadonly('FEE_CHANGE.PERFORMED_AT', this.dataContext.performedAt),
      changeFrom: this._fc.createReadonly('FEE_CHANGE.CHANGE_FROM', this.dataContext.changeFrom),
      changeType: this._fc.createReadonly('FEE_CHANGE.CHANGE_TYPE', this.dataContext.changeType),
      changeValue: this._fc.createReadonly('FEE_CHANGE.CHANGE_VALUE', this.dataContext.changeValue),
      retroactive: this._fc.createReadonly('FEE_CHANGE.IS_RETROACTIVE', this.dataContext.retroactive),
      retroactiveFrom: this._fc.createReadonly('FEE_CHANGE.RETROACTIVE_FROM', this.dataContext.retroactiveFrom),
      retroactiveTo: this._fc.createReadonly('FEE_CHANGE.RETROACTIVE_TO', this.dataContext.retroactiveTo),
      webUserName: this._fc.createReadonly('LOG.USERNAME', this.dataContext.webUserName),
    });
  }

  get showRetroactive(): boolean {
    return this.form && this.form.controls['retroactive'].value;
  }
}
